<template>
    <div class="py-4 container-fluid">
      <div class=" row">
        <div class="col-12">
            <div class="card">
                <div v-if="isLoading" class="text-center p-5">
                    <div class="spinner-border text-success" role="status">
                        <span class="visually-hidden">Cargando...</span>
                    </div>
                </div>
                <div v-else class="table-responsive p-0">
                    <ClientsTable :clients="clients" :invoices="pagedRows"/>
                </div>

                <div v-if="!isLoading && totalRecords > 0" class="card-footer d-flex justify-content-between align-items-center">
                    <small class="text-muted">
                        Mostrando {{ (currentPage - 1) * itemsPerPage + 1 }}–{{ Math.min(currentPage * itemsPerPage, filteredRows.length) }} de {{ filteredRows.length }} registros filtrados.
                    </small>

                    <nav aria-label="Page navigation example">
                        <ul class="pagination pagination-info">
                            <li class="page-item" :class="{ disabled: currentPage === 1 }">
                                <a class="page-link" href="#" @click.prevent="changePage(currentPage - 1)">
                                    <i class="fa fa-angle-left"></i>
                                </a>
                            </li>
                            <li
                                class="page-item"
                                v-for="page in visiblePages"
                                :key="page"
                                :class="{ active: currentPage === page }"
                            >
                                <a class="page-link" href="#" @click.prevent="changePage(page)">{{ page }}</a>
                            </li>
                            <li class="page-item" :class="{ disabled: currentPage === totalPages }">
                                <a class="page-link" href="#" @click.prevent="changePage(currentPage + 1)">
                                    <i class="fa fa-angle-right"></i>
                                </a>
                            </li>
                        </ul>
                    </nav>
                </div>

            </div>
        </div>
      </div>
    </div>
</template>

<script>
import axios from "axios";
import ClientsTable from "./components/NCTable.vue";
const companyId = localStorage.getItem('companyId');
export default {
    name: "InvoiceTablesView",
    components: {
        ClientsTable,
    },
    data() {
        return {
            clients: [],
            invoices: [], // Almacena TODAS las facturas cargadas desde el backend
            searchTerm: '',
            currentPage: 1,
            itemsPerPage: 10,
            totalRecords: 0, // Total de registros cargados (invoices.length)
            isLoading: true,
        };
    },
    computed: {
        // 1. FILTRADO: Aplica la búsqueda a todas las facturas
        filteredRows() {
            if (!this.searchTerm) {
                return this.invoices;
            }
            const searchLower = this.searchTerm.toLowerCase();
            return this.invoices.filter(invoice => {
                // Combina campos relevantes para la búsqueda
                const fullNumber = invoice.full_number ? invoice.full_number.toString().toLowerCase() : '';
                const clientName = invoice.client_name ? invoice.client_name.toString().toLowerCase() : '';
                const clientNit = invoice.client_nit ? invoice.client_nit.toString().toLowerCase() : '';

                return fullNumber.includes(searchLower) ||
                       clientName.includes(searchLower) ||
                       clientNit.includes(searchLower);
            });
        },
        // 2. PAGINACIÓN: Muestra solo los elementos de la página actual
        pagedRows() {
            const start = (this.currentPage - 1) * this.itemsPerPage;
            const end = start + this.itemsPerPage;
            return this.filteredRows.slice(start, end);
        },
        // 3. Totales
        totalPages() {
            return Math.ceil(this.filteredRows.length / this.itemsPerPage);
        },
        // 4. Mostrar solo algunas páginas en el menú
        visiblePages() {
            const range = 2; // Mostrar 2 páginas antes y 2 después de la actual
            let start = Math.max(1, this.currentPage - range);
            let end = Math.min(this.totalPages, this.currentPage + range);

            const pages = [];
            for (let i = start; i <= end; i++) {
                pages.push(i);
            }
            return pages;
        }
    },
    watch: {
        // Al cambiar el término de búsqueda, volvemos a la página 1
        searchTerm() {
            this.currentPage = 1;
        }
    },
    mounted() {
        this.fetchClientsAndInvoices();
    },
    methods:{
        // === 1. Lógica de Carga (Carga TODOS los datos) ===
        async fetchClientsAndInvoices() {
            this.isLoading = true;
            const token = localStorage.getItem("accessToken");
            const config = {
                headers: { 'Authorization': `Bearer ${token}` }
            };

            // Usamos un array para almacenar todos los clientes, pues se necesitan para el mapeo
            const clientMap = {};

            // 1. Cargar Clientes
            try {
                const clientsResult = await axios.get(this.$store.state.apiURL + 'clients', config);
                this.clients = clientsResult.data;
                this.clients.forEach(client => {
                    clientMap[client.id] = {
                        client_name: client.name || client.business_name,
                        client_nit: client.nit,
                        client_type: client.type
                    };
                });
            } catch (error) {
                this.handleSessionError(error);
                this.isLoading = false;
                return;
            }

            // 2. Cargar TODAS las Facturas (sin parámetros de paginación)
            axios.get(this.$store.state.apiURL + 'invoice_headers?company='+companyId+'&invoice_type=NOTA_CREDITO', config)
                .then((result) => {
                    const invoicesData = result.data; // Asumimos que es el array completo

                    this.invoices = invoicesData.map(invoice => {
                        const clientInfo = clientMap[invoice.client_id] || {};

                        // Conversión de tipos (necesaria)
                        const subtotal = parseFloat(invoice.subtotal);
                        const sub_total_brut = parseFloat(invoice.sub_total_brut);
                        const total_taxes = parseFloat(invoice.total_taxes);
                        const total_discount= parseFloat(invoice.total_discount);
                        const total = parseFloat(invoice.total);

                        return {
                            ...invoice,
                            subtotal,
                            total_taxes,
                            total,
                            sub_total_brut,
                            total_discount,
                            // Campos enriquecidos para la tabla
                            client_name: invoice.client_name || clientInfo.client_name || 'N/A',
                            client_nit: invoice.client_nit || clientInfo.client_nit || 'N/A',
                            client_type: clientInfo.client_type || 'N/A'
                        };
                    });

                    // 🛑 PASO CLAVE: Establecer el total basado en lo que se cargó
                    this.totalRecords = this.invoices.length;

                })
                .catch(error => {
                    this.handleSessionError(error);
                })
                .finally(() => {
                    this.isLoading = false;
                });
        },

        // === 2. Controladores de la Paginación y Redirección ===
        changePage(page) {
            if (page >= 1 && page <= this.totalPages) {
                this.currentPage = page;
            }
        },
        toCreate() {
             this.$router.push('/create-invoice');
        },
        // Manejo de error de sesión (adaptado de tu código)
        handleSessionError(error) {
            const self = this;
            if (error.response && error.response.status === 401) {
                 self.$swal({
                    title: 'Sesión',
                    text: 'El tiempo de sesión a vencido, ingresa nuevamente.',
                    icon:'warning',
                    showCancelButton: false,
                    confirmButtonColor:'#bf4f51',
                    showCloseButton: true,
                    showLoaderOnConfirm: false
                }).then(function(){
                    localStorage.removeItem('user');
                    localStorage.removeItem('accessToken');
                    self.$router.push('/signin');
                });
            } else {
                 console.error("Error al obtener datos:", error);
            }
        }
    },
};
</script>